import React, {useEffect } from 'react';
import ConcessionScreenController from './ConcessionScreenController.js';
import PersonWalkingIcon from '../assets/walking_icon_white.svg';

// Helper function to format time without leading zeros
const formatTimeNoLeadingZero = (date, timeZone) => {
  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone || 'America/New_York'
  };
  return date.toLocaleTimeString('en-US', options);
};

function ConcessionScreen() {
  // Hide navigation for screen view and add responsive styles
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-nav-screen-view';
    style.textContent = `
      /* Hide all navigation elements */
      nav, .navbar, .sidebar, .nav-sidebar, .navigation, 
      .nav-container, .nav-wrapper, .main-nav, .side-nav,
      [class*="nav"], [class*="sidebar"], [id*="nav"], [id*="sidebar"] {
        display: none !important;
      }
      
      /* Make content take full width */
      .main-content, .content, .page-content, #root > div {
        margin: 0 !important;
        padding: 0 !important;
        padding-left: clamp(1rem, 2vw, 2rem) !important;
        padding-right: clamp(1rem, 2vw, 2rem) !important;
        width: 100vw !important;
        max-width: none !important;
      }
      
      /* Ensure body and html are full screen */
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        overflow: hidden !important;
        background: linear-gradient(135deg, #13274F 0%, #1a3260 100%) !important;
      }

      /* Responsive font sizing based on viewport */
      .responsive-text-xs { font-size: clamp(0.8rem, 1.2vw, 1.4rem); }
      .responsive-text-sm { font-size: clamp(1rem, 1.5vw, 1.8rem); }
      .responsive-text-md { font-size: clamp(1.2rem, 2vw, 2.5rem); }
      .responsive-text-lg { font-size: clamp(1.6rem, 2.8vw, 3.8rem); }
      .responsive-text-xl { font-size: clamp(2.5rem, 4vw, 6rem); }
      .responsive-text-xxl { font-size: clamp(3rem, 5vw, 8rem); }
      .responsive-badge { font-size: clamp(0.8rem, 1.5vw, 1.35rem); }
      .responsive-badge-large { font-size: clamp(1.80rem, 2.65vw, 2.15rem); }
    `;
    
    document.head.appendChild(style);
    
    // Cleanup when component unmounts
    return () => {
      const styleElement = document.getElementById('hide-nav-screen-view');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <ConcessionScreenController>
      {({ loading, concessionLocationStaticData, currentSection, 
          league, venue, token, concessionLocationSections, 
          nearbyCurrentSection, currentWaitTimeData, theme, 
        setLoading }) => {
        
        // Get wait time status
        const getWaitStatus = (minutes) => {
          if (minutes < 10) return { text: 'SHORT QUEUE', color: '#24BA00' };
          return { text: 'LONG QUEUE', color: '#E50037' };
        };

        const formatQueueTime = (minutes) => {
          return minutes > 15 ? '15+' : minutes;
        };

        // Calculate total time (wait + walk)
        const getTotalTime = (sectionId, queueTime) => {
          const walkTime = nearbyCurrentSection && nearbyCurrentSection[sectionId] 
            ? nearbyCurrentSection[sectionId].walkTime 
            : 0;
          return queueTime + walkTime;
        };

        // Get current section wait time data
        const getCurrentSectionData = () => {
          const currentData = currentWaitTimeData.find(data => data.section === currentSection);
          return currentData ? currentData.queueTime : 0;
        };

        // Get nearby sections for grid display
        const getNearbyGridSections = () => {
          if (!nearbyCurrentSection) return [];
          
          const nearbyIds = Object.keys(nearbyCurrentSection).map(id => parseInt(id));
          const sectionsWithData = nearbyIds.map(sectionId => {
            const waitData = currentWaitTimeData.find(data => data.section === sectionId);
            const nearbyData = nearbyCurrentSection[sectionId];
            
            return {
              id: sectionId,
              queueTime: waitData ? waitData.queueTime : 0,
              direction: nearbyData.direction,
              walkTime: nearbyData.walkTime,
              totalTime: getTotalTime(sectionId, waitData ? waitData.queueTime : 0)
            };
          });

          // Group sections by direction
          const leftSections = sectionsWithData.filter(s => s.direction === 'left');
          const rightSections = sectionsWithData.filter(s => s.direction === 'right');
          const upSections = sectionsWithData.filter(s => s.direction === 'up');
          const downSections = sectionsWithData.filter(s => s.direction === 'down');
          const otherSections = sectionsWithData.filter(s => 
            !['left', 'right', 'up', 'down'].includes(s.direction)
          );

          // Create a pool of all remaining sections after left/right placement
          const remainingSections = [...upSections, ...downSections, ...otherSections];

          // Arrange in 2x2 grid: [top-left, top-right, bottom-left, bottom-right]
          const gridOrder = [null, null, null, null];
          
          // Fill left positions first (positions 0 and 2)
          let leftIndex = 0;
          for (const section of leftSections) {
            if (leftIndex === 0) {
              gridOrder[0] = section; // top-left
              leftIndex++;
            } else if (leftIndex === 1) {
              gridOrder[2] = section; // bottom-left
              leftIndex++;
            }
          }
          
          // Fill right positions (positions 1 and 3)
          let rightIndex = 0;
          for (const section of rightSections) {
            if (rightIndex === 0) {
              gridOrder[1] = section; // top-right
              rightIndex++;
            } else if (rightIndex === 1) {
              gridOrder[3] = section; // bottom-right
              rightIndex++;
            }
          }
          
          // Fill any remaining empty positions with remaining sections
          for (let i = 0; i < gridOrder.length; i++) {
            if (gridOrder[i] === null && remainingSections.length > 0) {
              gridOrder[i] = remainingSections.shift();
            }
          }

          console.log('Grid sections:', gridOrder.filter(Boolean).map(s => ({ id: s.id, direction: s.direction })));
          return gridOrder.filter(Boolean); // Filter out any null values
        };

        // Find section with shortest total time
        // Returns null if any section is missing data or current section has no queue time
        const getShortestTotalTimeSection = () => {
          const currentSectionTime = getCurrentSectionData();
          
          // If current section has no data, don't show best choice
          if (currentSectionTime === 0) return { id: null, totalTime: Infinity };

          // If any nearby section has no data, don't show best choice
          const allNearbySections = getNearbyGridSections();
          const hasInactiveSensor = allNearbySections.length !== Object.keys(nearbyCurrentSection || {}).length ||
            allNearbySections.some(s => s.queueTime === 0);
          if (hasInactiveSensor) return { id: null, totalTime: Infinity };

          const allSections = [
            { id: currentSection, totalTime: currentSectionTime },
            ...allNearbySections
          ];

          return allSections.reduce((shortest, current) => 
            current.totalTime < shortest.totalTime ? current : shortest
          );
        };

        // Get direction arrow and text
        const getDirectionDisplay = (direction) => {
          const directionMap = {
            'left': { arrow: '←', text: 'Left' },
            'right': { arrow: '→', text: 'Right' },
            'up': { arrow: '↑', text: 'Upstairs' },
            'down': { arrow: '↓', text: 'Downstairs' }
          };
          return directionMap[direction] || { arrow: '', text: direction };
        };
        {/* Initial View while init static data call happens */}
        if (loading) {
          return (
            <div style={{ 
              height: '100vh', 
              width: '100vw',
              background: '#fafefe',
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div>Loading...</div>
                <p style={{ marginTop: '1.5rem' }}>Loading queue times...</p>
              </div>
            </div>
          );
        }
        if (!concessionLocationStaticData || !currentWaitTimeData) {
          return (
            <div style={{ 
              height: '100vh',
              width: '100vw',
              background: '#fafefe',
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <h3>No Data Available</h3>
              </div>
            </div>
          );
        }

        const currentSectionQueueTime = getCurrentSectionData();
        const gridSections = getNearbyGridSections().filter(s => s.queueTime > 0);
        const shortestSection = getShortestTotalTimeSection();
        const bannerBorderColor = currentSectionQueueTime > 0 
          ? ((shortestSection.id === currentSection) ? '#24BA00' : getWaitStatus(currentSectionQueueTime).color) 
          : '#EAAA00';

        return (
          <div style={{ 
            height: '100vh',
            width: '100vw',
            background: theme.background.primary,
            color: theme.text.primary,
            padding: 'clamp(1rem, 2.5vw, 2.5rem)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            boxSizing: 'border-box'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'clamp(1rem, 2.5vw, 2.5rem)',
              minHeight: 'clamp(8rem, 15vh, 12rem)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* <img 
                  src={require(`../clients/${league}/${venue}/assets/stadium_logo.png`)}
                  alt="Stadium Logo"
                  style={{ 
                    height: 'clamp(10rem, 14vh, 14rem)',
                    width: 'auto',
                    marginRight: 'clamp(3rem, 5vw, 4rem)',
                    marginTop: 'clamp(0.5rem, 1.5vw, 1.25rem)'
                  }}
                /> */}
              <div style={{
                  borderRadius: theme.logo?.borderRadius || 'clamp(0.8rem, 1.5vw, 1.25rem)',
                  boxShadow: theme.logo?.boxShadow || 'none',
                  background: theme.logo?.background || 'transparent',
                  padding: theme.logo?.padding || '0',
                  marginBottom: '0rem',
                  marginTop: 'clamp(0.5rem, 1.5vw, 1.25rem)',
                  display: 'inline-block'
                }}>
                  <img 
                    src={require(`../clients/${concessionLocationStaticData.logoPath}`)}
                    alt={concessionLocationStaticData.name}
                    style={{ 
                      width: 'clamp(44rem, 50vw, 50rem)',
                      maxHeight: 'clamp(11rem, 17vh, 17rem)',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'fill'
                    }}
                  />
                </div>
              </div>
              
              {/* Current Time/Date */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  fontSize: 'clamp(2.5rem, 4vw, 6rem)',
                  paddingBottom: '0.25rem',
                  fontWeight: 'bold',
                  color: theme.text.primary
                }}
                className="responsive-text-xl"
                >
                  {formatTimeNoLeadingZero(new Date(), concessionLocationStaticData.timeZone)}
                </div>
                <div style={{ 
                  fontSize: 'clamp(1rem, 1.5vw, 1.8rem)',
                  color: theme.text.secondary || theme.text.primary,
                  opacity: 0.8 
                }}
                className="responsive-text-sm"
                >
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric',
                    timeZone: concessionLocationStaticData.timeZone || 'America/New_York'
                  })}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 'clamp(0.5rem, 1.5vw, 1.2rem)',
              minHeight: 0
            }}>
              {/* You Are Here Banner - Current Section */}
              <div style={{
                background: theme.background.banner,
                backdropFilter: 'blur(30px) saturate(200%)',
                WebkitBackdropFilter: 'blur(30px) saturate(200%)',
                border: `clamp(3px, 0.4vw, 6px) solid ${bannerBorderColor}`,
                borderRadius: 'clamp(1rem, 2vw, 1.7rem)',
                padding: 'clamp(1rem, 2vw, 1.5rem) clamp(2rem, 4vw, 3.4rem)',
                marginBottom: 'clamp(1.4rem, 2.4vw, 1.8rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: `
                  0 0 30px ${bannerBorderColor}30,
                  0 5px 20px rgba(0, 0, 0, 0.5),
                  inset 0 1px 0 rgba(255, 255, 255, 0.2)
                `,
                position: 'relative',
                minHeight: 'clamp(4rem, 8vh, 6rem)'
              }}>
                {/* Show BEST CHOICE badge if current section has the shortest total time */}
                {shortestSection.id === currentSection && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '90%',
                    transform: 'translate(-50%, -50%)',
                    background: '#24BA00',
                    color: '#081B4C',
                    padding: 'clamp(0.05rem, 0.3vw, 0.1rem) clamp(0.6rem, 2vw, 1rem)',
                    borderRadius: 'clamp(0.4rem, 1.2vw, 1.3rem)',
                    fontWeight: 'bold',
                    boxShadow: '0 6px 11px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    fontSize: 'clamp(1.80rem, 2.65vw, 2.15rem)'
                  }}
                  className="responsive-badge-large"
                  >
                    BEST CHOICE
                  </div>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    background: theme.background.banner_here,
                    color: theme.text.banner_text,
                    padding: 'clamp(0.7rem, 1.55vw, 1.1rem) clamp(1.8rem, 3.1vw, 2.7rem)',
                    borderRadius: 'clamp(1rem, 2vw, 1.9rem)',
                    fontSize: 'clamp(1.2rem, 2vw, 1.7rem)',
                    fontWeight: 'bold',
                    marginRight: 'clamp(2.4rem, 4vw, 3.4rem)',
                    border: `clamp(2px, 0.2vw, 3px) solid ${bannerBorderColor}`,
                    boxShadow: '0 6px 11px rgba(0,0,0,0.3)',
                    whiteSpace: 'nowrap'
                  }}>
                    YOU ARE HERE
                  </div>
                  <div>
                    <h2 style={{ 
                      margin: 0,
                      fontWeight: 'bold',
                      fontSize: 'clamp(3rem, 4.5vw, 7rem)',
                      color: theme.text.banner_section
                    }}
                    >
                      Section {currentSection}
                    </h2>
                    <p style={{ 
                      margin: '0.4rem 0 0 0',
                      fontSize: 'clamp(1.4rem, 2.3vw, 3rem)',
                      color: theme.text.banner_subtitle
                    }}
                    >
                      Current Location
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 'clamp(0.5rem, 1vw, 0.75rem)',
                  fontSize: 'clamp(5rem, 9.5vw, 9.5rem)',
                  fontWeight: 'bold',
                  color: (shortestSection.id === currentSection) ? '#24BA00' : getWaitStatus(currentSectionQueueTime).color,
                  lineHeight: 1
                }}>
                  {currentSectionQueueTime > 0 && (
                    <div>
                      {formatQueueTime(currentSectionQueueTime)}
                      <span style={{
                        fontSize: 'clamp(2.2rem, 3.2vw, 2.8rem)',
                        fontWeight: 'normal',
                        marginLeft: 'clamp(0.5rem, 1vw, 1rem)',
                        marginRight: 'clamp(0.4rem, 1.0vw, 1.0rem)',
                        color: theme.text.banner_mins,
                        opacity: 1.0
                      }}>
                        {' '}min{currentSectionQueueTime !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Dynamic 2x2 Grid for Nearby Sections */}
              <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: 'clamp(1.4rem, 2.4vw, 2.4rem)',
                alignContent: 'start',
                minHeight: 0
              }}>
                {gridSections.map((section, index) => {
                  const status = getWaitStatus(section.queueTime);
                  const isShortestWait = section.id === shortestSection.id;
                  const directionDisplay = getDirectionDisplay(section.direction);
                  
                  return (
                    <div
                      key={section.id}
                      style={{
                        background: theme.background.grid,
                        backdropFilter: 'blur(20px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                        border: `clamp(1px, 0.15vw, 2px) solid ${isShortestWait ? '#24BA00' : status.color}`,
                        borderRadius: 'clamp(1.2rem, 1.8vw, 1.5rem)',
                        padding: 'clamp(1.5rem, 3vw, 2.5rem) clamp(1rem, 2.5vw, 2.1rem)',
                        position: 'relative',
                        boxShadow: `
                          0 8px 32px rgba(0, 0, 0, 0.4),
                          0 0 20px ${status.color}20,
                          inset 0 1px 0 rgba(255, 255, 255, 0.1)
                        `,
                        transition: 'all 0.3s ease',
                        cursor: 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: 0
                      }}
                    >
                      {isShortestWait && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '81.5%',
                          WebkitTextStroke: '0.3px currentColor',
                          transform: 'translate(-50%, -50%)',
                          background: '#24BA00',
                          color: '#081B4C',
                          padding: 'clamp(0.25rem, 0.6vw, 0.4rem) clamp(1.25rem, 2.5vw, 2rem)',
                          borderRadius: 'clamp(0.8rem, 1.5vw, 1.7rem)',
                          fontWeight: 'bold',
                          boxShadow: '0 6px 11px rgba(0,0,0,0.3)',
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                          fontSize: 'clamp(1.4rem, 2.2vw, 1.9rem)',
                          letterSpacing: '0.05em'
                        }}>
                          BEST CHOICE
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ 
                            margin: 0,
                            paddingBottom: '0.3rem',
                            fontWeight: 'bold',
                            fontSize: 'clamp(2rem, 3.5vw, 4.5rem)',
                            color: theme.text.grid_section
                          }}
                          className="responsive-text-lg"
                          >
                            Section {section.id}
                          </h3>
                          <p style={{ 
                            margin: 'clamp(0.3rem, 0.5vw, 0.4rem) 0 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(1.0rem, 1.4vw, 1.4rem)',
                            fontSize: 'clamp(1.2rem, 2vw, 2.5rem)',
                            color: theme.text.grid_subtitle
                          }}
                          className="responsive-text-md"
                          >
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 'clamp(0.6rem, 1.2vw, 1rem)' 
                            }}>
                              <span style={{ fontSize: '1.6em', fontWeight: 'bold' }}>
                                {directionDisplay.arrow}
                              </span>
                              <span>
                                {directionDisplay.text}
                              </span>
                              {/* <span style={{ opacity: 0.0 }}>·</span> */}
                              <span>{section.walkTime} min</span>
                            </div>
                            <img 
                              src={PersonWalkingIcon}
                              alt="Walking"
                              style={{
                                  height: 'clamp(1.1rem, 3.5vw, 3rem)',
                                  width: 'auto',
                                  opacity: 1.0,
                                  flexShrink: 0
                              }}
                            />
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ 
                            fontSize: 'clamp(4rem, 7vw, 7rem)',
                            fontWeight: 'bold',
                            lineHeight: 1,
                            color: isShortestWait ? '#24BA00' : status.color
                          }}>
                            {formatQueueTime(section.queueTime)}
                            <span style={{
                              fontSize: 'clamp(2rem, 3vw, 2.6rem)',
                              fontWeight: 'normal',
                              marginLeft: 'clamp(0.3rem, 0.8vw, 0.6rem)',
                              color: theme.text.grid_mins,
                              opacity: 1.0
                            }}>
                              {' '}min{section.queueTime !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom padding */}
            <div style={{ height: 'clamp(1.8rem, 3.8vw, 2.4rem)' }}></div>
          </div>
        );
      }}
    </ConcessionScreenController>
  );
}

export default ConcessionScreen;